import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import {
  Battery, Bike, Globe, Leaf,
  CheckCircle2, Zap, TrendingUp, ArrowRight
} from 'lucide-react';
import { useInView } from '../components/UI';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const heroStats = [
  { value: '$6/day', label: 'Validated fleet payment per bike' },
  { value: '$1,872', label: 'Annual total revenue per bike (312 days)' },
  { value: '2.0×', label: 'Average daily BaaS swaps per bike' },
  { value: '$264.8M', label: 'Projected annual revenue by 2036' },
];

const steps = [
  { num: '01', title: 'Rider Onboarding', desc: 'Verified rider receives an electric motorcycle and begins daily commercial operations. Guarantor and digital identity checks protect against default from day one.' },
  { num: '02', title: 'Daily Operations', desc: 'Rider earns income transporting passengers or goods. Swaps depleted batteries at Azura stations in under two minutes. 2.0 swaps per day across 312 operating days per year.' },
  { num: '03', title: 'Daily Payment', desc: '$6 collected daily through digital payment. 312 operating days per year per bike generates $1,872 in total yearly fleet revenue before the 15% default buffer.' },
  { num: '04', title: 'How Revenue Is Used', desc: 'Revenue allocated across eight categories: 30% operations, 20% expansion, 15% retained earnings, 10% reducing the AZT token supply, 10% investor distribution, and operational reserves.' },
];

const streams = [
  {
    num: 'STREAM 01', title: 'Electric Fleet Operations', icon: <Bike size={22} />,
    img: '/images/bike1.png',
    desc: "Electric motorcycles put on the road under Azura's fleet programme generate predictable daily payment revenue. Riders pay $6 per operating day across 312 annual operating days. Each motorcycle put on the road generates $1,872 in annual total revenue before the 15% default buffer is applied.",
    tags: ['Validated Model', 'Cashless', 'Real-Time Tracking', 'Insurance-Backed'],
    metrics: [{ v: '$6', l: 'Daily payment per bike' }, { v: '312', l: 'Operating days per year' }, { v: '$1,872', l: 'Annual total revenue per bike' }, { v: '15%', l: 'Default buffer (base case)' }],
    note: 'At 500 bikes, base case fleet revenue is $795.6K. At 100,000 bikes it reaches $159.1M. At 500,000 bikes: $795.6M.',
    quote: undefined,
  },
  {
    num: 'STREAM 02', title: 'Battery-as-a-Service', icon: <Battery size={22} />,
    img: '/images/bike2.png',
    desc: 'Azura owns and operates a network of battery swap stations, each serving 30 bikes and holding 30 fully charged batteries. Riders swap in under two minutes, eliminating downtime and removing grid dependency. Each fleet bike generates 2.0 swaps per day at $1.00 per swap. Walk-in riders pay $1.20 per swap.',
    tags: ['Sub-2-Min Swap', 'No Grid Dependency', 'Walk-In Revenue', 'Scales With Fleet'],
    metrics: [{ v: '2.0×', l: 'Daily swaps per bike' }, { v: '$1.00', l: 'Fleet swap rate' }, { v: '$1.20', l: 'Walk-in swap rate' }, { v: '80/20', l: 'Fleet vs walk-in split' }],
    note: 'BaaS generates $324.5K at 500 bikes, $3.24M at 5,000 bikes, and $64.9M at 100,000 bikes.',
    quote: undefined,
  },
  {
    num: 'STREAM 03', title: 'Electric Vehicle Sales', icon: <Zap size={22} />,
    img: '/images/bike3.png',
    desc: 'Azura sells electric motorcycles directly to individuals, commercial operators, logistics companies, and institutions at $2,040 per unit. Profit margin rises from 16.7% in Phase 1 to 41.2% at full manufacturing scale. EV sales also drive use of the battery-swap network.',
    tags: ['$2,040 Sale Price', 'Tiered Margin 16.7%–41.2%', 'Drives BaaS Volume'],
    metrics: [{ v: '$2,040', l: 'Sale price per unit' }, { v: '$1,700', l: 'Phase 1 cost per unit' }, { v: '16.7%', l: 'Phase 1 profit margin' }, { v: '41.2%', l: 'Full manufacturing profit margin' }],
    note: null,
    quote: undefined,
  },
  {
    num: 'STREAM 04', title: 'Electric Ride-Hailing', icon: <Globe size={22} />,
    img: '/images/bike4.png',
    desc: "Azura Ride launches in Phase 2 (2028) as Azura's own electric ride-hailing platform, built directly on the battery-swap network. Because Azura owns the energy supply chain, Azura Ride operates at long-term lower costs than petrol-based competitors, without subsidising operations.",
    tags: ['Phase 2: 2028', 'Own Platform', 'BaaS-Integrated'],
    metrics: [],
    note: null,
    quote: 'Because Azura owns the energy infrastructure, Azura Ride has a cost advantage competitors cannot replicate without building their own BaaS network from scratch.',
  },
  {
    num: 'STREAM 05', title: 'Carbon Finance', icon: <Leaf size={22} />,
    img: '/images/bike5.png',
    desc: 'Every electric motorcycle put on the road displaces a petrol-powered alternative, generating measurable CO₂ reductions. Azura qualifies for Gold Standard and Verra VCS carbon credit frameworks, with registration beginning at launch.',
    tags: ['Gold Standard Eligible', 'Verra VCS Eligible', 'DFI Access', 'ESG Mandates'],
    metrics: [{ v: '~1.5T', l: 'CO₂ saved per bike per year' }, { v: '5', l: 'UN SDGs directly aligned' }, { v: '$450B+', l: 'Annual global climate finance' }, { v: '2', l: 'Carbon registries targeted' }],
    note: null,
    quote: undefined,
  },
];

const allocation = [
  { label: 'Operations', pct: 30 },
  { label: 'Expansion / Marketing', pct: 20 },
  { label: 'Retained Earnings', pct: 15 },
  { label: 'Reducing AZT Supply', pct: 10 },
  { label: 'Investor Distribution', pct: 10 },
  { label: 'Insurance', pct: 5 },
  { label: 'Sinking Fund', pct: 5 },
  { label: 'R&D / Sankofa Foundry', pct: 5 },
];

const riskControls = [
  'Mandatory guarantor support', 'Rider verification procedures',
  'Digital identity validation', 'Real-time vehicle tracking',
  'Daily payment collection', 'Digital payment integration',
  'Fleet insurance coverage', 'Asset protection policies',
];

const whyPoints = [
  'First-mover position in the uncontested Ibadan market, where no major EV operator has made this city a primary focus',
  'Only player combining owned BaaS infrastructure, ride-hailing platform, and community token in a single ecosystem',
  'Phased path to full local manufacturing, with no competitor on the continent holding a credible manufacturing roadmap',
  'Nigerian-founded, a long-term advantage for government partnerships, eligibility for development-finance funding, and community trust',
  'Revenue-funded manufacturing, where each stage of the manufacturing roadmap is self-funded by the operating revenue of the previous stage',
  'BaaS as a moat: every swap station built makes the network more valuable and harder to displace',
];

const Img: React.FC<{ src: string; alt: string; className?: string; fallback?: string }> = ({
  src, alt, className, fallback = 'linear-gradient(135deg,#1E3A2F,#2B5243)'
}) => (
  <img src={src} alt={alt} className={className}
    onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = fallback; }} />
);

/* ══ ECOSYSTEM PAGE ══ */
const Ecosystem: React.FC = () => {

  const { ref: statsRef, inView: statsInView } = useInView();
  const { ref: stepsRef, inView: stepsInView } = useInView();
  const { ref: allocRef, inView: allocInView } = useInView();
  const { ref: whyRef, inView: whyInView } = useInView();



  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      <section className="relative bg-bg-dark overflow-hidden">

        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />

          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">

          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link
              to="/"
              className="text-white/60 hover:text-accent-amber transition text-xs font-semibold"
            >
              Home
            </Link>

            <span className="text-white/70">/</span>

            <span className="text-accent-amber text-xs font-semibold">
              Ecosystem
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl">
            The Azura
            <span className="text-accent-amber"> Ecosystem</span>
          </h1>

          <p className="mt-6 max-w-2xl text-white/70 text-md leading-8">
            Discover how our electric vehicles, battery swapping, ride-hailing,
            and local manufacturing work together to create a smarter and more
            sustainable future for mobility across Africa.
          </p>

        </div>

      </section>
      {/* ══ HERO ══ */}
      <section className="bg-bg-cream pt-16 pb-0 px-6 relative overflow-hidden ">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full border border-white/[0.05]" />
          <div className="absolute top-1/2 -left-20 w-64 h-64 bg-accent-amber opacity-[0.06]" style={{ borderRadius: '60% 40% 55% 45% / 50% 60% 40% 55%' }} />
          <div className="absolute bottom-0 right-16 w-40 h-24 opacity-[0.1]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="lg:col-span-6 pb-16">
              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em]">The Ecosystem</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="font-figtree font-black text-3xl md:text-5xl leading-[1.05] tracking-tight mb-5">
                Five revenue streams.<br />
                One integrated <span className="text-accent-amber italic">platform.</span><br />
                Built for Africa.
              </motion.h1>

              <motion.p variants={fadeUp} className="font-exo  text-base leading-relaxed max-w-lg mb-10">
                Azura is not a single product. It is a layered operations company where each revenue stream strengthens the others: fleet operations feed BaaS volume, BaaS enables ride-hailing, and the whole platform funds manufacturing.
              </motion.p>

              <motion.div ref={statsRef} variants={fadeUp} className="grid grid-cols-2 gap-3">
                {heroStats.map((s, i) => (
                  <div key={s.label}
                    className={`p-5 rounded-2xl border border-white/[0.08] bg-gray-100 transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${i * 80}ms` }}>
                    <p className="font-figtree font-black text-2xl text-accent-amber mb-1 tracking-tight">{s.value}</p>
                    <p className="font-bold text-xs  leading-snug">{s.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Hero image — flush bottom */}
            <motion.div initial="hidden" animate="visible" variants={fadeLeft} className="lg:col-span-6 relative self-end">
              <div className="relative rounded-t-3xl overflow-hidden aspect-[4/3]">
                <img src="/images/bike6.png" alt="Azura fleet operations" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-bg-dark/10 to-transparent" />

              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ══ HOW IT WORKS ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={stepsRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-dark border border-border group">
                <Img src="/images/bike7.png" alt="How Azura works" className="w-full h-full object-cover grayscale contrast-[1.1] transition-transform duration-700 group-hover:scale-[1.03]" fallback="linear-gradient(135deg,#1E3A2F,#11221B)" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/50 to-transparent" />
                <div className="absolute bottom-5 left-5">
                  <span className="bg-white/90 backdrop-blur text-xs font-bold uppercase tracking-widest text-bg-dark px-3 py-1.5 rounded-full">The Model</span>
                </div>
              </div>
            </motion.div>

            <div className="lg:col-span-7">
              <div className={`mb-10 transition-all duration-700 ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center -space-x-1">
                    <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                    <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">How It Works</span>
                </div>
                <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
                  The model in <span className="text-accent-amber italic">four steps.</span>
                </h2>
              </div>

              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-gradient-to-b from-accent-amber/60 via-accent-amber/20 to-transparent" />
                <div className="space-y-8">
                  {steps.map((st, i) => (
                    <div key={st.num}
                      className={`flex gap-5 items-start pl-12 relative transition-all duration-700 ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                      style={{ transitionDelay: `${i * 110}ms` }}>
                      <div className="absolute left-0 w-10 h-10 rounded-full bg-bg-dark flex items-center justify-center font-bold text-accent-amber text-xs shrink-0 z-10">
                        {st.num}
                      </div>
                      <div>
                        <h3 className="font-figtree font-bold text-sm text-bg-dark mb-1">{st.title}</h3>
                        <p className="font-exo text-xs text-neutral-500 leading-relaxed">{st.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA 2 ══ */}
      <section className="py-16 px-6 bg-white border-y border-border text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-2xl md:text-3xl text-bg-dark tracking-tight mb-3">
            Five engines, <span className="text-accent-amber italic">one token behind them.</span>
          </h2>
          <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-6 max-w-xl mx-auto">
            AZT is the fastest way to get exposure to every revenue stream on this page at once.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
            Invest Now <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ REVENUE STREAMS ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Revenue Streams</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              Each stream earns independently.<br />
              <span className="text-accent-amber italic">Each amplifies the others.</span>
            </h2>
          </div>

          <div className="space-y-8">
            {streams.map((st, i) => (
              <div key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Image */}
                <div className="lg:col-span-4">
                  <div className="relative rounded-2xl overflow-hidden aspect-[3/2] shadow-card border border-border group">
                    <Img src={st.img} alt={st.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs text-white/70 tracking-widest uppercase mb-1">{st.num}</p>
                      <p className="font-figtree font-black text-white text-lg leading-tight">{st.title}</p>
                    </div>
                    <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-bg-dark/80 backdrop-blur border border-white/10 flex items-center justify-center text-accent-amber">
                      {st.icon}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-8 flex flex-col justify-center">
                  <div className="bg-white border border-border rounded-2xl p-8 shadow-card mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                    <span className="absolute bottom-5 right-6 font-figtree font-black text-[5rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>

                    <p className="font-exo text-sm text-neutral-600 leading-relaxed mb-6">{st.desc}</p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {st.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 rounded-full bg-neutral-100 border border-border text-xs font-bold uppercase tracking-widest text-bg-dark">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {st.metrics.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 border-t border-border">
                        {st.metrics.map((m, mi) => (
                          <div key={mi}>
                            <p className="font-figtree font-black text-xl text-accent-amber mb-0.5 tracking-tight">{m.v}</p>
                            <p className="text-xs text-neutral-500 uppercase tracking-widest leading-snug">{m.l}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {st.quote && (
                      <div className="mt-5 flex gap-4 p-5 bg-neutral-50 rounded-xl border border-border">
                        <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
                        <div>
                          <span className="font-serif text-3xl text-accent-amber/20 leading-none select-none">"</span>
                          <p className="font-exo text-sm text-bg-dark leading-relaxed italic">{st.quote}</p>
                          <p className="text-xs text-neutral-400 mt-2 tracking-widest uppercase">Azura Mobility · Whitepaper v2.0</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {st.note && (
                    <div className="px-5 py-3.5 rounded-xl bg-accent-amber-light border border-accent-amber/20 flex items-start gap-3">
                      <TrendingUp size={14} className="text-accent-amber shrink-0 mt-0.5" />
                      <p className="font-exo text-xs text-bg-dark leading-relaxed">{st.note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ REVENUE ALLOCATION ══ */}
      <section className="py-20 px-6 bg-bg-dark relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full border border-white/[0.04]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div ref={allocRef} className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-14">
            <div className={`lg:col-span-6 transition-all duration-700 ${allocInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Revenue Allocation</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-4">
                How every dollar of total<br />
                <span className="text-accent-amber italic">revenue is used.</span>
              </h2>
              <p className="font-exo text-white/70 text-sm leading-relaxed">
                Azura allocates all operational revenue across eight categories before the default buffer — balancing operations, fleet growth, investor returns, and long-term ecosystem development.
              </p>
            </div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden aspect-video shadow-dark border border-white/10 group">
                <Img src="/images/bike1.png" alt="Revenue allocation" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-br from-bg-dark/70 to-accent-amber/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-figtree font-black text-4xl text-accent-amber">8</p>
                    <p className="text-xs text-white/70 uppercase tracking-widest">Revenue categories</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              {allocation.map((a, i) => (
                <div key={a.label}
                  className={`transition-all duration-700 ${allocInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                  style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="font-figtree font-bold text-sm text-white">{a.label}</span>
                    <span className="font-bold text-accent-amber text-sm">{a.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: allocInView ? `${a.pct * (100 / 30)}%` : 0 }}
                      transition={{ duration: 1.1, ease: 'easeOut', delay: i * 0.06 }}
                      className="h-full bg-accent-amber rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-7">
              <h3 className="font-figtree font-black text-lg text-white mb-2">Reducing Default Risk</h3>
              <p className="font-exo text-xs text-white/70 leading-relaxed mb-6">Eight overlapping controls protect fleet revenue from non-payment risk.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {riskControls.map((r, i) => (
                  <div key={i}
                    className={`flex items-start gap-2.5 transition-all duration-500 ${allocInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                    style={{ transitionDelay: `${200 + i * 50}ms` }}>
                    <CheckCircle2 size={14} className="text-accent-amber shrink-0 mt-0.5" />
                    <span className="font-exo text-xs text-white/60 leading-relaxed">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHY IT WORKS ══ */}
      <section className="py-20 px-6 bg-bg-cream border-t border-border">
        <div ref={whyRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
            <div className={`lg:col-span-5 transition-all duration-700 ${whyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Why It Works</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-5">
                Long-term advantages competitors
                <span className="text-accent-amber italic"> cannot replicate.</span>
              </h2>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-4">
                Most EV companies in Africa own one piece of the stack. Azura owns all of them. Owning every part of the process like this creates cost, data, and revenue advantages that take years and significant capital to replicate.
              </p>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-8">
                The $6/day payment model is not a projection. It was validated through field research in Ibadan, carefully tested against real rider income patterns, and backed by insurance and guarantor structures.
              </p>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-card border border-border group">
                <Img src="/images/bike2.png" alt="Why Azura works" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="bg-white/90 backdrop-blur text-xs font-bold uppercase tracking-widest text-bg-dark px-3 py-1.5 rounded-full">
                    Field-validated · Ibadan
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-3">
              {whyPoints.map((point, i) => (
                <div key={i}
                  className={`flex items-start gap-3.5 p-4 rounded-2xl bg-white border border-border shadow-card hover:border-bg-dark/25 hover:shadow-card-hover transition-all duration-200 ${whyInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="w-5 h-5 rounded-full bg-accent-amber-light border border-accent-amber/30 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 size={11} className="text-bg-dark" />
                  </div>
                  <span className="font-exo text-sm text-neutral-600 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
            Five revenue engines. <span className="text-accent-amber italic">One token behind them all.</span>
          </h2>
          <p className="font-exo text-white/55 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            AZT gives you a stake across every stream in this ecosystem. The Strategic Round opens October 15.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Buy AZT <ArrowRight size={13} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Ecosystem;